import React, { useCallback, useEffect, useState } from "react";
import { Drawer, Table, Tag, Typography } from "antd";
import { ScoringJob, fetchJobs } from "./api";

const { Text } = Typography;
const POLL_MS = 15000;
const STATUS_COLOR: Record<string, string> = { PENDING: "default", RUNNING: "processing", FINISHED: "success", FAILED: "error" };

const number = (v: number | null) => (v === null || v === undefined ? "" : v.toLocaleString());

/** The scoring jobs, newest first, with the load counts that tell whether an upload added anything. */
const JobsTab: React.FC<{ refreshKey: number }> = ({ refreshKey }) => {
  const [rows, setRows] = useState<ScoringJob[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState({ current: 1, pageSize: 20 });
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState<ScoringJob | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchJobs(page.current, page.pageSize);
      setRows(data.rows);
      setTotal(data.total);
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  // Keep polling while something is still running.
  useEffect(() => {
    if (!rows.some((r) => r.status === "PENDING" || r.status === "RUNNING")) return;
    const timer = setInterval(load, POLL_MS);
    return () => clearInterval(timer);
  }, [rows, load]);

  return (
    <div style={{ padding: 24 }}>
      <Table
        size="small"
        rowKey="id"
        loading={loading}
        dataSource={rows}
        onRow={(job) => ({ onClick: () => setSelected(job), style: { cursor: "pointer" } })}
        pagination={{
          current: page.current,
          pageSize: page.pageSize,
          total,
          showSizeChanger: true,
          pageSizeOptions: [10, 20, 50, 100],
          onChange: (current, pageSize) => setPage({ current, pageSize }),
          onShowSizeChange: (_current, pageSize) => setPage({ current: 1, pageSize }),
        }}
        columns={[
          { title: "When", dataIndex: "createdAt", render: (v: string) => new Date(v).toLocaleString() },
          { title: "Who", dataIndex: "userEmail" },
          { title: "File", dataIndex: "fileName", render: (v: string | null, job) => v ?? <Text type="secondary">{job.mode === "rescore" ? "rescore only" : ""}</Text> },
          { title: "Status", dataIndex: "status", render: (v: string, job) => (
              <>
                <Tag color={STATUS_COLOR[v] || "default"}>{v}</Tag>
                {job.nothingNew && job.status === "FINISHED" && <Tag color="warning">nothing new or changed in this file</Tag>}
                {job.errorMessage && <Text type="danger">{job.errorMessage}</Text>}
              </>
            ) },
          { title: "Rows in file", dataIndex: "rowsInFile", align: "right", render: number },
          { title: "New", dataIndex: "rowsInserted", align: "right", render: number },
          { title: "Updated", dataIndex: "rowsUpdated", align: "right", render: number },
          { title: "Unchanged", dataIndex: "rowsUnchanged", align: "right", render: number },
          { title: "Episodes scored", dataIndex: "episodesScored", align: "right", render: number },
          { title: "Warnings", dataIndex: "feedHealthWarnings", render: (w: ScoringJob["feedHealthWarnings"]) => (w?.length ? <Tag color="warning">{w.length} feed health</Tag> : "none") },
          { title: "Duration", dataIndex: "durationSeconds", align: "right", render: (v: number | null) => (v ? `${v}s` : "") },
        ]}
      />

      <Drawer open={Boolean(selected)} onClose={() => setSelected(null)} width={640} title={selected ? `Job ${selected.id}` : ""}>
        {selected && (
          <>
            {selected.feedHealthWarnings?.length > 0 && (
              <Table
                size="small"
                style={{ marginBottom: 16 }}
                title={() => "Feed health warnings (latest month against the trailing three)"}
                rowKey={(w) => `${w.platform}|${w.channel}`}
                dataSource={selected.feedHealthWarnings}
                pagination={false}
                columns={[
                  { title: "Platform", dataIndex: "platform" },
                  { title: "Channel", dataIndex: "channel" },
                  { title: "Month", dataIndex: "month" },
                  { title: "Ratio", dataIndex: "hov_ratio" },
                ]}
              />
            )}
            <Text strong>Step log</Text>
            <pre style={{ fontSize: 12, whiteSpace: "pre-wrap" }}>{JSON.stringify(selected.steps, null, 2)}</pre>
          </>
        )}
      </Drawer>
    </div>
  );
};

export default JobsTab;
